"use client";
import React from "react";
import Script from "next/script";
import Image from "next/image";
import Link from "next/link";
import dynamic from "next/dynamic";
import Navigation from "../../components/Navigation";
import RandomBackground from "../../components/RandomBackground";

const FloatingAnchorMenu = dynamic(
  () => import("@/app/components/FloatingAnchorMenu"),
  { ssr: false },
);

function GlassCard({
  children,
  className = "",
}: Readonly<{ children: React.ReactNode; className?: string }>) {
  return (
    <div
      className={`rounded-2xl border border-white/10 bg-black/60 backdrop-blur-glass shadow-glass !bg-black/60 ${className}`}
    >
      {children}
    </div>
  );
}

export default function BuyingGuidePage() {
  const floatingMenuItems = [
    { href: "#overview", text: "Overview" },
    { href: "#why-weatherproof", text: "Why Weatherproof Matters" },
    { href: "#gauge-guide", text: "Gauge Guide" },
    { href: "#safety-ratings", text: "Safety Ratings" },
    { href: "#types", text: "Cord Types" },
    { href: "#recommendations", text: "Recommendations" },
    { href: "#protection", text: "Rain Block® Protection" },
    { href: "#checklist", text: "Buyer Checklist" },
    { href: "#shop-now", text: "Shop" },
  ];

  const guideSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline:
      "Are Outdoor Extension Cords Waterproof? Complete Christmas Lights Buying Guide",
    description:
      "Comprehensive guide to selecting and protecting weatherproof extension cords for Christmas light installations",
    image: "https://rainblock.net/hydroponics.webp",
    author: {
      "@type": "Organization",
      name: "Rain Block®",
    },
    datePublished: "2024-01-01",
    dateModified: "2024-01-09",
    articleBody:
      "Complete guide covering extension cord gauges, outdoor ratings, safety considerations, and Rain Block® protection solutions",
  };

  return (
    <>
      <Script
        id="guide-schema"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(guideSchema) }}
      />

      <div className="relative min-h-screen text-white overflow-hidden">
        <Navigation />
        {/* Fixed background */}
        <RandomBackground />
        {/* Readability overlays */}
        <div
          aria-hidden
          className="fixed inset-0 -z-10 bg-gradient-to-b from-black/75 via-black/25 to-black/10"
        />
        <div
          aria-hidden
          className="fixed inset-0 -z-10 pointer-events-none"
          style={{
            background:
              "radial-gradient(80% 60% at 50% 20%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.15) 100%)",
          }}
        />
        <FloatingAnchorMenu items={floatingMenuItems} scrollShift={-180} />

        {/* Content */}
        <main className="relative z-10 pt-40 pb-24 px-8 xl:pt-24">
          <div className="mx-auto max-w-4xl">
            {/* Header */}
            <div id="overview" className="mb-12">
              <h1 className="text-5xl font-extrabold mb-6">
                Are Outdoor Extension Cords Waterproof?{" "}
                <span className="text-[#66ccff]">Complete Guide for Christmas Lights</span>
              </h1>
              <p className="text-lg text-white/85 mb-4">
                Most extension cords are water-resistant, not fully waterproof.
                This guide helps you choose the right outdoor cord and protect
                plug connections from rain, snow, and moisture.
              </p>
              <p className="text-white/80 mb-4">
                Need plug-level protection now? Use a{" "}
                <Link
                  href="/waterproof-extension-cord-connector"
                  className="text-[#66ccff] underline hover:text-[#87dcff]"
                >
                  waterproof extension cord connector
                </Link>{" "}
                page, then{" "}
                <Link href="/shop" className="text-[#66ccff] underline hover:text-[#87dcff]">
                  shop protection kits
                </Link>{" "}
                and follow the{" "}
                <Link
                  href="/guides/how-to-weatherproof-christmas-lights-and-decorations"
                  className="text-[#66ccff] underline hover:text-[#87dcff]"
                >
                  weatherproof setup guide
                </Link>
                .
              </p>
              <div className="flex items-center gap-4 text-white/85">
                <span>ðŸ“‹ Complete Guide</span>
                <span>â€¢</span>
                <span>â±ï¸ 8 min read</span>
              </div>
            </div>

            <section className="mb-12">
              <GlassCard className="p-8">
                <h2 className="text-3xl font-bold mb-4">Cord Only vs Cord + Rain Block® Protection</h2>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[620px] text-left text-white/85">
                    <thead>
                      <tr className="border-b border-white/20 text-white">
                        <th className="py-3 pr-4">Category</th>
                        <th className="py-3 pr-4">Outdoor Cord Only</th>
                        <th className="py-3">Cord + Rain Block®</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-white/10">
                        <td className="py-3 pr-4">Plug connection sealing</td>
                        <td className="py-3 pr-4">Limited</td>
                        <td className="py-3">High</td>
                      </tr>
                      <tr className="border-b border-white/10">
                        <td className="py-3 pr-4">Rain and meltwater protection</td>
                        <td className="py-3 pr-4">Moderate</td>
                        <td className="py-3">High</td>
                      </tr>
                      <tr className="border-b border-white/10">
                        <td className="py-3 pr-4">Best use case</td>
                        <td className="py-3 pr-4">Dry or mild weather runs</td>
                        <td className="py-3">Wet weather and seasonal displays</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4">Install time per connection</td>
                        <td className="py-3 pr-4">None</td>
                        <td className="py-3">3-5 seconds</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </GlassCard>
            </section>

            {/* Table of Contents */}
            <GlassCard className="p-8 mb-12 border-[#66ccff]/30 bg-[#66ccff]/5">
              <h2 className="text-2xl font-bold mb-4">Table of Contents</h2>
              <ul className="space-y-2 text-white">
                <li>
                  <a href="#why-weatherproof" className="hover:text-[#66ccff]">
                    â€¢ Why Weatherproof Extension Cords Matter
                  </a>
                </li>
                <li>
                  <a href="#gauge-guide" className="hover:text-[#66ccff]">
                    â€¢ Understanding Extension Cord Gauges
                  </a>
                </li>
                <li>
                  <a href="#safety-ratings" className="hover:text-[#66ccff]">
                    â€¢ Safety Ratings & Standards
                  </a>
                </li>
                <li>
                  <a href="#types" className="hover:text-[#66ccff]">
                    â€¢ Types of Weatherproof Cords
                  </a>
                </li>
                <li>
                  <a href="#recommendations" className="hover:text-[#66ccff]">
                    â€¢ Top Product Recommendations
                  </a>
                </li>
                <li>
                  <a href="#protection" className="hover:text-[#66ccff]">
                    â€¢ Extra Protection with Rain Block<sup className="tm-reg">&reg;</sup>
                  </a>
                </li>
                <li>
                  <a href="#checklist" className="hover:text-[#66ccff]">
                    â€¢ Buyer&apos;s Checklist
                  </a>
                </li>
              </ul>
            </GlassCard>

            <GlassCard className="mb-12 border-[#66ccff]/30 bg-[#66ccff]/5 p-8">
              <h2 className="mb-4 text-2xl font-bold">Related Waterproofing Resources</h2>
              <div className="flex flex-wrap gap-3">
                <Link
                  href="/waterproof-extension-cord-connector"
                  className="rounded-full border border-white/20 bg-white/10 px-4 py-2 font-semibold text-white hover:bg-white/15"
                >
                  Waterproof Extension Cord Connector
                </Link>
                <Link
                  href="/watertight-extension-cord-connectors"
                  className="rounded-full border border-white/20 bg-white/10 px-4 py-2 font-semibold text-white hover:bg-white/15"
                >
                  Watertight Extension Cord Connectors
                </Link>
                <Link
                  href="/weatherproof-gasket"
                  className="rounded-full border border-white/20 bg-white/10 px-4 py-2 font-semibold text-white hover:bg-white/15"
                >
                  Weatherproof Gasket Guide
                </Link>
                <Link
                  href="/waterproof-outdoor-christmas-lights"
                  className="rounded-full border border-white/20 bg-white/10 px-4 py-2 font-semibold text-white hover:bg-white/15"
                >
                  Waterproof Outdoor Christmas Lights
                </Link>
              </div>
            </GlassCard>

            {/* Why Weatherproof */}
            <section id="why-weatherproof" className="mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Why Weatherproof Extension Cords Matter
              </h2>
              <GlassCard className="p-8 mb-6">
                <p className="text-white mb-4">
                  Standard indoor extension cords are not designed for outdoor
                  conditions. Exposure to rain, snow, and freezing temperatures
                  can lead to:
                </p>
                <ul className="space-y-3 text-white/85">
                  <li className="flex gap-3">
                    <span className="text-[#66ccff] font-bold">âš ï¸</span>
                    <span>
                      <strong>Electrical hazards:</strong> Water damage
                      increases the risk of short circuits and electrical shock
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#66ccff] font-bold">âš ï¸</span>
                    <span>
                      <strong>Safety breaches:</strong> Damaged insulation
                      exposes live wires, creating fire and electrocution risks
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#66ccff] font-bold">âš ï¸</span>
                    <span>
                      <strong>Equipment failure:</strong> Christmas lights going
                      out mid-display ruins your holiday magic
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#66ccff] font-bold">âš ï¸</span>
                    <span>
                      <strong>Property damage:</strong> Electrical fires can
                      damage your home and surrounding areas
                    </span>
                  </li>
                </ul>
              </GlassCard>
              <p className="text-white/85">
                Outdoor-rated extension cords have reinforced insulation,
                weather-resistant covers, and are tested to withstand moisture
                and temperature extremes. They&apos;re an essential investment for
                any outdoor electrical setup.
              </p>
            </section>

            {/* Gauge Guide */}
            <section id="gauge-guide" className="mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Understanding Extension Cord Gauges
              </h2>
              <p className="text-white/85 mb-6">
                Extension cord gauge (AWG - American Wire Gauge) determines how
                much electrical current it can safely handle. The lower the
                gauge number, the thicker the wire and the more power it can
                carry.
              </p>

              <div className="space-y-4 mb-6">
                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    12 AWG (Best for Christmas Lights)
                  </h3>
                  <p className="text-white mb-2">
                    <strong>Amperage:</strong> 15A maximum
                  </p>
                  <p className="text-white mb-2">
                    <strong>Max Length:</strong> Up to 150 feet
                  </p>
                  <p className="text-white/85">
                    <strong>Best for:</strong> Standard Christmas light
                    displays, outdoor power tools, moderate power demands
                  </p>
                </GlassCard>

                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    14 AWG (Standard Use)
                  </h3>
                  <p className="text-white mb-2">
                    <strong>Amperage:</strong> 15A maximum
                  </p>
                  <p className="text-white mb-2">
                    <strong>Max Length:</strong> Up to 50 feet
                  </p>
                  <p className="text-white/85">
                    <strong>Best for:</strong> Short runs, light-duty
                    applications, indoor/outdoor combined use
                  </p>
                </GlassCard>

                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    16 AWG (Light Duty)
                  </h3>
                  <p className="text-white mb-2">
                    <strong>Amperage:</strong> 10A maximum
                  </p>
                  <p className="text-white mb-2">
                    <strong>Max Length:</strong> Up to 25 feet
                  </p>
                  <p className="text-white/85">
                    <strong>Best for:</strong> Indoor use, short distances,
                    low-power devices
                  </p>
                </GlassCard>
              </div>

              <GlassCard className="p-6 border-[#66ccff]/30 bg-[#66ccff]/5 mb-6">
                <p className="text-white">
                  <strong className="text-[#66ccff]">ðŸ’¡ Pro Tip:</strong> For
                  Christmas lights, use a 12 AWG cord if your run exceeds 50
                  feet. Longer distances with thinner wire cause voltage drop,
                  dimming your lights.
                </p>
              </GlassCard>
            </section>

            {/* Safety Ratings */}
            <section id="safety-ratings" className="mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Safety Ratings & Standards
              </h2>

              <div className="space-y-4 mb-6">
                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    UL Certification (UL 1581)
                  </h3>
                  <p className="text-white/85">
                    Underwriters Laboratories certification ensures the cord
                    meets rigorous safety standards for outdoor use. Always look
                    for &quot;UL Listed&quot; on the packaging.
                  </p>
                </GlassCard>

                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    GFCI Protection
                  </h3>
                  <p className="text-white/85">
                    Ground Fault Circuit Interrupter outlets automatically shut
                    off power if moisture is detected. Highly recommended for
                    outdoor Christmas light setups.
                  </p>
                </GlassCard>

                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    Cold Weather Rating
                  </h3>
                  <p className="text-white/85">
                    Look for cords rated to -40Â°C or lower. Standard extension
                    cords become brittle in cold, increasing breakage and
                    insulation damage.
                  </p>
                </GlassCard>

                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    Moisture Resistance (Wet Location Rated)
                  </h3>
                  <p className="text-white/85">
                    Only use cords marked &quot;for wet locations&quot; outdoors. This
                    indicates the insulation and jacket are water-resistant.
                  </p>
                </GlassCard>
              </div>
            </section>

            {/* Types of Weatherproof Cords */}
            <section id="types" className="mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Types of Weatherproof Extension Cords
              </h2>

              <div className="space-y-4 mb-6">
                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    SJTW (Standard Outdoor)
                  </h3>
                  <p className="text-white mb-2">
                    <strong>Material:</strong> Rubber jacket with vinyl
                    insulation
                  </p>
                  <p className="text-white mb-2">
                    <strong>Temperature Range:</strong> -40Â°C to 60Â°C
                  </p>
                  <p className="text-white/85">
                    <strong>Best For:</strong> Most Christmas light
                    applications, outdoor power tools
                  </p>
                </GlassCard>

                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    SOOW (Heavy Duty Outdoor)
                  </h3>
                  <p className="text-white mb-2">
                    <strong>Material:</strong> Oil-resistant rubber jacket
                  </p>
                  <p className="text-white mb-2">
                    <strong>Temperature Range:</strong> -40Â°C to 60Â°C
                  </p>
                  <p className="text-white/85">
                    <strong>Best For:</strong> Demanding conditions, job sites,
                    long-term outdoor use
                  </p>
                </GlassCard>

                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    LED-Specific Outdoor Cords
                  </h3>
                  <p className="text-white mb-2">
                    <strong>Features:</strong> Designed for low-amperage LED
                    strings
                  </p>
                  <p className="text-white mb-2">
                    <strong>Advantage:</strong> Smaller, more flexible than
                    standard cords
                  </p>
                  <p className="text-white/85">
                    <strong>Best For:</strong> Modern LED Christmas light setups
                  </p>
                </GlassCard>

                <GlassCard className="p-6">
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    Retractable/Storage Cords
                  </h3>
                  <p className="text-white mb-2">
                    <strong>Features:</strong> Wind-up storage, space-saving
                    design
                  </p>
                  <p className="text-white mb-2">
                    <strong>Advantage:</strong> Easy to store and organize
                  </p>
                  <p className="text-white/85">
                    <strong>Best For:</strong> Seasonal use, convenient storage
                    between holidays
                  </p>
                </GlassCard>
              </div>
            </section>

            {/* Top Recommendations */}
            <section id="recommendations" className="mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Top Product Recommendations
              </h2>

              <div className="space-y-4 mb-6">
                <GlassCard className="p-6">
                  <Image
                    src="/12gauge.jpg"
                    alt="12 AWG Extension Cord"
                    width={400}
                    height={200}
                    className="rounded-lg mb-4 h-48 w-auto"
                  />
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    ðŸ¥‡ Best Overall: Commercial-Grade 12 AWG SJTW
                  </h3>
                  <p className="text-white mb-3">
                    <strong>Specifications:</strong> 12 AWG, 100+ ft, UL Listed,
                    -40Â°C rated
                  </p>
                  <p className="text-white/85 mb-3">
                    The safest choice for extensive Christmas light displays.
                    Thick insulation, proven durability, and industry standard.
                  </p>
                  <p className="text-white/75 mb-4">
                    <strong>Price Range:</strong> $30-50
                  </p>
                  <a
                    href="https://www.amazon.com/s?k=12+AWG+SJTW+outdoor+extension+cord"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block py-2 px-6 rounded-lg border border-[#66ccff]/50 bg-[#66ccff]/10 text-[#66ccff] font-semibold hover:bg-[#66ccff]/20 transition-all duration-300"
                  >
                    View on Amazon â†’
                  </a>
                </GlassCard>

                <GlassCard className="p-6">
                  <Image
                    src="/14gauge.jpg"
                    alt="14 AWG Extension Cord"
                    width={400}
                    height={200}
                    className="rounded-lg mb-4 h-48 w-auto"
                  />
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    ðŸ¥ˆ Best for Budget: 14 AWG Outdoor Rated
                  </h3>
                  <p className="text-white mb-3">
                    <strong>Specifications:</strong> 14 AWG, 50-75 ft, UL
                    Listed, wet location rated
                  </p>
                  <p className="text-white/85 mb-3">
                    Affordable option for moderate displays (under 50 feet).
                    Still provides weather protection at half the cost.
                  </p>
                  <p className="text-white/75 mb-4">
                    <strong>Price Range:</strong> $15-25
                  </p>
                  <a
                    href="https://www.amazon.com/s?k=14+AWG+outdoor+rated+extension+cord"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block py-2 px-6 rounded-lg border border-[#66ccff]/50 bg-[#66ccff]/10 text-[#66ccff] font-semibold hover:bg-[#66ccff]/20 transition-all duration-300"
                  >
                    View on Amazon â†’
                  </a>
                </GlassCard>

                <GlassCard className="p-6">
                  <Image
                    src="/14retract.jpg"
                    alt="14 AWG Retractable Extension Cord"
                    width={400}
                    height={200}
                    className="rounded-lg mb-4 h-48 w-auto"
                  />
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    ðŸ¥‰ Best for Long Runs: Retractable 12 AWG
                  </h3>
                  <p className="text-white mb-3">
                    <strong>Specifications:</strong> 12 AWG, 100 ft, retractable
                    reel, UL Listed
                  </p>
                  <p className="text-white/85 mb-3">
                    Perfect for installations exceeding 75 feet. Retractable
                    design keeps storage tidy year-round.
                  </p>
                  <p className="text-white/75 mb-4">
                    <strong>Price Range:</strong> $40-70
                  </p>
                  <a
                    href="https://www.amazon.com/s?k=retractable+extension+cord+12+AWG"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block py-2 px-6 rounded-lg border border-[#66ccff]/50 bg-[#66ccff]/10 text-[#66ccff] font-semibold hover:bg-[#66ccff]/20 transition-all duration-300"
                  >
                    View on Amazon â†’
                  </a>
                </GlassCard>

                <GlassCard className="p-6">
                  <Image
                    src="/12prem.jpg"
                    alt="14 AWG Retractable Extension Cord"
                    width={400}
                    height={200}
                    className="rounded-lg mb-4 h-48 w-auto"
                  />
                  <h3 className="text-xl font-bold text-[#66ccff] mb-3">
                    â­ Premium Choice: Heavy Duty SOOW
                  </h3>
                  <p className="text-white mb-3">
                    <strong>Specifications:</strong> 12 AWG, 100+ ft,
                    oil-resistant, extreme cold rated
                  </p>
                  <p className="text-white/85 mb-3">
                    Maximum durability for harsh climates. Professional-grade
                    construction handles extreme weather and frequent use.
                  </p>
                  <p className="text-white/75 mb-4">
                    <strong>Price Range:</strong> $60-100
                  </p>
                  <a
                    href="https://www.amazon.com/s?k=SOOW+extension+cord+outdoor+12+AWG"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block py-2 px-6 rounded-lg border border-[#66ccff]/50 bg-[#66ccff]/10 text-[#66ccff] font-semibold hover:bg-[#66ccff]/20 transition-all duration-300"
                  >
                    View on Amazon â†’
                  </a>
                </GlassCard>
              </div>
            </section>

            {/* Extra Protection */}
            <section id="protection" className="mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Add Extra Protection with Rain Block<sup className="tm-reg">&reg;</sup>
              </h2>
              <GlassCard className="p-8 border-[#66ccff]/50 bg-[#66ccff]/10">
                <div className="flex flex-row justify-start gap-4 mb-6">
                  <Image
                    src="/plug2.jpg"
                    alt="14 AWG Retractable Extension Cord"
                    width={400}
                    height={200}
                    style={{ height: "auto" }}
                    className="rounded-lg mb-4 h-48 w-auto"
                  />
                  <Image
                    src="/install.jpg"
                    alt="14 AWG Retractable Extension Cord"
                    width={400}
                    height={200}
                    className="rounded-lg mb-4 h-48 w-auto"
                  />
                </div>

                <p className="text-white mb-4">
                  The connection between your extension cord and appliance, light, or decoration is the most vulnerable spot for water damage.
                </p>
                <p className="text-white mb-6">
                  <strong className="text-[#66ccff]">Rain Block<sup className="tm-reg">&reg;</sup></strong> is a
                  gel gasket that inserts between the extension cord plug and
                  your appliance, light, or decoration plug to provide 100%
                  waterproof protection:
                </p>
                <ul className="space-y-3 text-white/85 mb-6">
                  <li className="flex gap-3">
                    <span className="text-[#66ccff] font-bold">âœ“</span>
                    <span>
                      Super-soft gel material creates a waterproof seal between
                      extension cord and appliance plugs
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#66ccff] font-bold">âœ“</span>
                    <span>
                      Prevents water, snow, and ice from entering the connection
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#66ccff] font-bold">âœ“</span>
                    <span>
                      Works with any extension cord and appliance (Christmas
                      lights, outdoor decorations, power tools, etc.)
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#66ccff] font-bold">âœ“</span>
                    <span>Installation takes 3-5 seconds per connection</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-[#66ccff] font-bold">âœ“</span>
                    <span>Reusable for 5+ years of seasonal use</span>
                  </li>
                </ul>
                <a
                  href="/shop"
                  className="rounded-full btn-cta px-5 py-3 font-extrabold text-white transition-all duration-300"
                  style={{ textShadow: "none" }}
                >
                  Shop Now
                </a>
              </GlassCard>
            </section>

            {/* Buyer&apos;s Checklist */}
            <section id="checklist" className="mb-12">
              <h2 className="text-3xl font-bold mb-4">Buyer&apos;s Checklist</h2>
              <GlassCard className="p-8">
                <p className="text-white mb-6">
                  Use this checklist when selecting a weatherproof extension
                  cord:
                </p>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 text-white/85 cursor-pointer hover:text-white">
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded accent-[#66ccff]"
                    />
                    <span>â˜ UL Listed for outdoor/wet locations</span>
                  </label>
                  <label className="flex items-center gap-3 text-white/85 cursor-pointer hover:text-white">
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded accent-[#66ccff]"
                    />
                    <span>â˜ 12 AWG for runs over 50 feet</span>
                  </label>
                  <label className="flex items-center gap-3 text-white/85 cursor-pointer hover:text-white">
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded accent-[#66ccff]"
                    />
                    <span>â˜ Cold-weather rated (-40Â°C or lower)</span>
                  </label>
                  <label className="flex items-center gap-3 text-white/85 cursor-pointer hover:text-white">
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded accent-[#66ccff]"
                    />
                    <span>
                      â˜ Heavy-duty insulation (rubber or oil-resistant)
                    </span>
                  </label>
                  <label className="flex items-center gap-3 text-white/85 cursor-pointer hover:text-white">
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded accent-[#66ccff]"
                    />
                    <span>â˜ Length matches or exceeds your run distance</span>
                  </label>
                  <label className="flex items-center gap-3 text-white/85 cursor-pointer hover:text-white">
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded accent-[#66ccff]"
                    />
                    <span>â˜ Using GFCI-protected outlet</span>
                  </label>
                  <label className="flex items-center gap-3 text-white/85 cursor-pointer hover:text-white">
                    <input
                      type="checkbox"
                      className="w-5 h-5 rounded accent-[#66ccff]"
                    />
                    <span>
                      â˜ Adding Rain Block<sup className="tm-reg">&reg;</sup> protection at connection points
                    </span>
                  </label>
                </div>
              </GlassCard>
            </section>

            {/* CTA */}
            <section id="shop-now" className="mb-12">
              <GlassCard className="p-12 text-center border-[#66ccff]/30 bg-[#66ccff]/10">
                <h2 className="text-3xl font-bold mb-4">
                  Ready to Weatherproof Your Christmas Display?
                </h2>
                <p className="text-lg text-white/85 mb-8 max-w-2xl mx-auto">
                  Choose the right extension cord from our guide, then add
                  Rain Block<sup className="tm-reg">&reg;</sup> for 100% waterproof protection at every connection
                  point.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/shop"
                    className="rounded-full btn-cta px-5 py-3 font-extrabold text-white transition-all duration-300"
                    style={{ textShadow: "none" }}
                  >
                    Shop Now
                  </a>
                  <a
                    href="/contact"
                    className="py-3 px-8 rounded-full border border-white/20 bg-white/10 text-white font-bold hover:bg-white/15 transition-all duration-300"
                  >
                    Ask Our Experts
                  </a>
                  <Link
                    href="/faq"
                    className="py-3 px-8 rounded-full border border-white/20 bg-white/10 text-white font-bold hover:bg-white/15 transition-all duration-300"
                  >
                    View FAQ
                  </Link>
                </div>
              </GlassCard>
            </section>
          </div>
        </main>
      </div>
    </>
  );
}







